import { Shell } from "./components/Shell.js";
import { Empty } from "./components/ui.js";
import { useRoute } from "./routes.js";
import { Dashboard } from "./screens/Dashboard.js";
import { LedgerDetail, Ledgers } from "./screens/Ledgers.js";
import { Outstanding } from "./screens/Outstanding.js";
import {
  BalanceSheet,
  CashBook,
  ProfitLoss,
  Ratios,
  ReportsIndex,
  TrialBalance,
} from "./screens/Reports.js";
import { Batches, CostCentres, Godowns } from "./screens/Advanced.js";
import { DeadStock, Dormant, InsightsIndex, TopCustomers, TopItems } from "./screens/Insights.js";
import { Graphs } from "./screens/Graphs.js";
import { More } from "./screens/More.js";
import { DayBook, SalesRegister, Search, StockSummary } from "./screens/Registers.js";
import { VoucherDetail } from "./screens/VoucherDetail.js";
import { Settings, Setup, Unlock } from "./screens/Setup.js";
import { PinPrompt } from "./screens/Lock.js";
import { useApp } from "./state.js";

export function App() {
  const { status, unlock, usePassphraseInstead, removeLock } = useApp();
  const route = useRoute();

  if (status.kind === "loading") {
    return <Splash>Opening…</Splash>;
  }
  if (status.kind === "needs-setup") {
    return <Setup />;
  }
  if (status.kind === "needs-pin") {
    return (
      <PinPrompt
        attemptsLeft={status.attemptsLeft}
        onUnlocked={(passphrase) => void unlock(passphrase)}
        onUsePassphrase={usePassphraseInstead}
        onWiped={() => {
          void removeLock();
          usePassphraseInstead();
        }}
      />
    );
  }
  if (status.kind === "needs-passphrase") {
    return <Unlock />;
  }
  if (status.kind === "error") {
    return <Splash>{status.message}</Splash>;
  }

  return (
    <Shell route={route}>
      {(() => {
        switch (route.id) {
          case "dashboard":
            return <Dashboard />;
          case "outstanding":
            return <Outstanding />;
          case "ledgers":
            return <Ledgers />;
          case "ledger":
            return route.param ? <LedgerDetail name={route.param} /> : <Ledgers />;
          case "voucher":
            return route.param ? <VoucherDetail id={route.param} /> : <DayBook />;
          case "stock":
            return <StockSummary />;
          case "daybook":
            return <DayBook />;
          case "sales":
            return <SalesRegister />;
          case "search":
            return <Search />;
          case "reports":
            return <ReportsIndex />;
          case "more":
            return <More />;
          case "balance-sheet":
            return <BalanceSheet />;
          case "profit-loss":
            return <ProfitLoss />;
          case "trial-balance":
            return <TrialBalance />;
          case "cash-book":
            return <CashBook />;
          case "ratios":
            return <Ratios />;
          case "graphs":
            return <Graphs />;
          case "insights":
            return <InsightsIndex />;
          case "dormant":
            return <Dormant />;
          case "top-customers":
            return <TopCustomers />;
          case "top-items":
            return <TopItems />;
          case "dead-stock":
            return <DeadStock />;
          case "cost-centres":
            return <CostCentres />;
          case "godowns":
            return <Godowns />;
          case "batches":
            return <Batches />;
          case "settings":
            return <Settings />;
          default:
            return <Empty>Unknown screen.</Empty>;
        }
      })()}
    </Shell>
  );
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-6 text-center text-sm text-ink-2">
      {children}
    </div>
  );
}
